import { useEffect, useMemo, useState } from 'react';
import { projectsApi } from '@/api/projects.api';
import type { SelectOption } from '@/components/ui/Select';
import type { ProjectProject } from '@/types/projects';
import { showApiError } from '@/utils/apiError';

export function useProjectOptions(pageSize = 200) {
  const [projects, setProjects] = useState<ProjectProject[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    projectsApi
      .listProjects({ page: 0, size: pageSize })
      .then((res) => setProjects(res.content))
      .catch((err) => showApiError(err, 'Failed to load projects'))
      .finally(() => setLoading(false));
  }, [pageSize]);

  const options: SelectOption[] = useMemo(
    () =>
      projects.map((project) => ({
        value: String(project.id),
        label: project.projectCode
          ? `${project.projectCode} — ${project.projectName}`
          : project.projectName,
      })),
    [projects],
  );

  const byId = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);

  const getProject = (id: number | string | null | undefined) => {
    if (id == null || id === '') return undefined;
    return byId.get(Number(id));
  };

  const getLabel = (id: number | string | null | undefined) => {
    const project = getProject(id);
    if (!project) return id != null && id !== '' ? `#${id}` : '—';
    return project.projectCode
      ? `${project.projectCode} — ${project.projectName}`
      : project.projectName;
  };

  return { projects, options, loading, getProject, getLabel, byId };
}
